import { audienceSegments } from './whoWeServe'
import { competitors } from './compare'
import { MANAGED_SERVICES } from '@gjl/voice/pricing'

/**
 * Single source for the site's primary navigation. Header (desktop dropdowns)
 * and MobileNav (grouped drawer) both render from this, so they cannot drift.
 */

export interface NavLink {
  label: string
  href: string
  description?: string
}

/** A titled column within a mega-menu group. */
export interface NavColumn {
  heading: string
  items: NavLink[]
}

export interface NavGroup {
  label: string
  /** Flat list of every link in the group. MobileNav and active-state
   *  detection read this; for mega-menu groups it is derived from `columns`
   *  so the two never drift. */
  items: NavLink[]
  /** When present, the desktop header renders this group as a multi-column
   *  mega menu instead of a single dropdown. */
  columns?: NavColumn[]
  footer?: { label: string; href: string }
}

/** Solutions is a mega menu: capabilities grouped by the job they do. */
export const solutionsColumns: NavColumn[] = [
  {
    heading: 'Websites & Applications',
    items: [
      {
        label: 'Websites for Music Organizations',
        href: '/services/websites',
        description: 'Designed for your organization, built and run for you',
      },
      {
        label: 'Application & Audition Funnels',
        href: '/services/application-funnels',
        description: 'Music-native applications with payments and review',
      },
    ],
  },
  {
    heading: 'Audience & Revenue',
    items: [
      {
        label: 'Email Marketing',
        href: '/services/email-marketing',
        description: 'Newsletters and season campaigns, one audience record',
      },
      {
        label: 'Donor Management',
        href: '/services/donor-management',
        description: 'Free to accept (0% platform fee); recurring giving, receipts, donor records',
      },
      {
        label: 'Ticketing',
        href: '/services/ticketing',
        description: 'Sell on your own site: 1.5% per ticket, QR check-in',
      },
    ],
  },
  {
    heading: 'Music & Creative',
    items: [
      {
        label: 'Music Manager',
        href: '/music-manager',
        description: 'Music library, rehearsal planning, ensembles (Canticle for churches)',
      },
      {
        label: 'Graphic Design',
        href: '/services/design',
        description: 'Brand identity, posters, programs, season campaigns',
      },
      {
        label: 'Photography',
        href: '/services/photography',
        description: 'Concert coverage, headshots, season photography days',
      },
    ],
  },
  {
    heading: 'Ongoing & Platform',
    items: [
      {
        label: 'Ongoing Partnership',
        href: '/services/digital-partnership',
        description: 'We keep running it with you after launch',
      },
      { label: 'The App', href: '/platform', description: 'The self-serve app your site runs on (fuguelab.app)' },
    ],
  },
]

export const solutionsGroup: NavGroup = {
  label: 'Solutions',
  items: solutionsColumns.flatMap((column) => column.items),
  columns: solutionsColumns,
  footer: { label: 'All services', href: '/services' },
}

export const doneForYouGroup: NavGroup = {
  label: 'Done For You',
  items: [
    // Website Care is a tier of Managed Website, not its own service, so it is
    // folded into the single Managed Website entry (see MANAGED_WEBSITE).
    ...MANAGED_SERVICES.filter((svc) => svc.id !== 'website-care').map((svc) => ({
      label: svc.name,
      href: `/services/done-for-you#${svc.id}`,
    })),
    {
      label: 'Concierge',
      href: '/services/done-for-you#concierge',
      description: 'Everything done for you, plus strategy',
    },
    {
      label: 'Consulting',
      href: '/packages#consulting',
      description: 'Strategy intensives and fractional digital direction',
    },
  ],
  footer: { label: 'All done-for-you services', href: '/services/done-for-you' },
}

export const whoWeServeGroup: NavGroup = {
  label: 'Who We Serve',
  items: audienceSegments.map((segment) => ({
    label: segment.name,
    href: `/who-we-help/${segment.slug}`,
  })),
  footer: { label: 'Everyone we help', href: '/who-we-help' },
}

export const compareGroup: NavGroup = {
  label: 'Compare',
  items: competitors.map((competitor) => ({
    label: `vs ${competitor.name}`,
    href: `/compare/${competitor.slug}`,
  })),
  footer: { label: 'All comparisons', href: '/compare' },
}

export const resourcesGroup: NavGroup = {
  label: 'Resources',
  items: [
    { label: 'Portfolio', href: '/portfolio' },
    { label: 'Blog & Case Studies', href: '/blog' },
    { label: 'Resource Library', href: '/resources' },
    { label: 'About', href: '/about' },
  ],
}

export const navGroups: NavGroup[] = [solutionsGroup, doneForYouGroup, whoWeServeGroup, compareGroup, resourcesGroup]

/** Top-level links that are not dropdowns. */
export const pricingLink: NavLink = { label: 'Pricing', href: '/packages' }
